import React, { useState, useEffect } from 'react';
import {
  Activity,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Monitor,
  Users,
  MessageSquare,
  Clock,
  TrendingUp,
  Heart,
  Thermometer,
  Gauge,
  Play,
  Pause,
  Download,
  Eye,
  Send,
  Settings,
  Calendar
} from 'lucide-react';
import apiClient from '../../../services/apiClient';
import toast from 'react-hot-toast';

const ECGDeviceMonitor = () => {
  const [ecgRequests, setEcgRequests] = useState([]);
  const [ecgResults, setEcgResults] = useState([]);
  const [upcomingBookings, setUpcomingBookings] = useState([]);
  const [stats, setStats] = useState({
    total_requests: 0,
    pending_requests: 0,
    completed_requests: 0,
    failed_requests: 0,
    total_results: 0,
    normal_results: 0,
    abnormal_results: 0,
    critical_results: 0,
    last_request_time: null,
    last_result_time: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [newRequestData, setNewRequestData] = useState({
    booking_id: '',
    notes: ''
  });

  useEffect(() => {
    document.title = 'MediFusion - ECG Device Monitor';
    fetchData();
    const interval = setInterval(fetchData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      setRefreshing(true);
      
      // Fetch endpoints individually with fallbacks for better error handling
      const promises = [
        apiClient.get('/ecg-integration/requests?limit=20').catch(e => ({ data: { requests: [] } })),
        apiClient.get('/ecg-integration/results?limit=10').catch(e => ({ data: { results: [] } })),
        apiClient.get('/ecg-integration/monitor/bookings?limit=50').catch(e => ({ data: [] })),
        apiClient.get('/ecg-integration/stats').catch(e => ({ 
          data: { 
            total_requests: 0, 
            pending_requests: 0, 
            completed_requests: 0, 
            failed_requests: 0,
            total_results: 0,
            normal_results: 0,
            abnormal_results: 0,
            critical_results: 0,
            last_request_time: null,
            last_result_time: null
          }
        }))
      ];
      
      const [requestsResponse, resultsResponse, bookingsResponse, statsResponse] = await Promise.all(promises);
      
      setEcgRequests(requestsResponse.data.requests || []);
      setEcgResults(resultsResponse.data.results || []);
      setUpcomingBookings(bookingsResponse.data || []);
      setStats(statsResponse.data || { 
        total_requests: 0, 
        pending_requests: 0, 
        completed_requests: 0, 
        failed_requests: 0,
        total_results: 0,
        normal_results: 0,
        abnormal_results: 0,
        critical_results: 0,
        last_request_time: null,
        last_result_time: null
      });
      
      setLoading(false);
      setError(null);
    } catch (error) {
      console.error('Error fetching ECG data:', error);
      setError('Some ECG data could not be loaded. Backend deployment may be in progress.');
      setLoading(false);
      if (!loading) {
        toast.error('Some ECG data could not be loaded');
      }
    } finally {
      setRefreshing(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'sent': return 'text-blue-600 bg-blue-100';
      case 'in_progress': return 'text-purple-600 bg-purple-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="h-4 w-4" />;
      case 'failed': return <AlertCircle className="h-4 w-4" />;
      case 'pending': return <Clock className="h-4 w-4" />;
      case 'sent': return <Send className="h-4 w-4" />;
      case 'in_progress': return <Activity className="h-4 w-4" />;
      case 'cancelled': return <Pause className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getResultStatusColor = (status) => {
    switch (status) {
      case 'normal': return 'text-green-600 bg-green-100';
      case 'abnormal': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      case 'inconclusive': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleSendRequest = async (requestId) => {
    try {
      await apiClient.post(`/ecg-integration/requests/${requestId}/send`);
      toast.success('ECG request sent successfully');
      fetchData();
    } catch (error) {
      console.error('Error sending ECG request:', error);
      toast.error('Failed to send ECG request');
    }
  };

  const handleFetchResult = async (requestId) => {
    try {
      await apiClient.post(`/ecg-integration/requests/${requestId}/fetch-result`);
      toast.success('ECG result fetched successfully');
      fetchData();
    } catch (error) {
      console.error('Error fetching ECG result:', error);
      toast.error('Failed to fetch ECG result');
    }
  };

  const handleCreateRequest = async () => {
    try {
      await apiClient.post('/ecg-integration/requests', newRequestData);
      toast.success('ECG request created successfully');
      setShowRequestModal(false);
      setNewRequestData({ booking_id: '', notes: '' });
      fetchData();
    } catch (error) {
      console.error('Error creating ECG request:', error);
      toast.error('Failed to create ECG request');
    }
  };

  const handleRequestECGForBooking = async (bookingId, patientId) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      if (!userData.id) {
        toast.error('User not authenticated');
        return;
      }

      const requestData = {
        booking_id: bookingId,
        patient_id: patientId,
        requested_by_user_id: userData.id,
        notes: `ECG request for booking ${bookingId}`
      };

      await apiClient.post('/ecg-integration/requests', requestData);
      toast.success(`ECG request created for booking ${bookingId}`);
      fetchData();
    } catch (error) {
      console.error('Error creating ECG request for booking:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to create ECG request';
      toast.error(errorMessage);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'N/A';
    const now = new Date();
    const date = new Date(dateString);
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-3">
          <RefreshCw className="h-6 w-6 text-blue-600 animate-spin" />
          <span className="text-gray-600">Loading ECG monitor data...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading ECG Data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-blue-600 p-2 rounded-lg">
            <Monitor className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">ECG Device Monitor</h1>
            <p className="text-gray-600">Monitor ECG workstation v3.4.7 integration</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={fetchData}
            disabled={refreshing}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center space-x-2"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          <button
            onClick={() => setShowRequestModal(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <Send className="h-4 w-4" />
            <span>New Request</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Requests</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total_requests}</p>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pending_requests}</p>
            </div>
            <div className="bg-yellow-100 p-3 rounded-full">
              <Clock className="h-6 w-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completed_requests}</p>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Results</p>
              <p className="text-2xl font-bold text-purple-600">{stats.total_results}</p>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <Heart className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Upcoming Bookings Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Upcoming Appointments - Request ECG</h3>
          <p className="text-sm text-gray-600 mt-1">Click "Request ECG" for any appointment to send ECG request to workstation</p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date & Time
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Doctor
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ECG Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {upcomingBookings.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <Calendar className="h-8 w-8 text-gray-400 mb-2" />
                      <p>No upcoming appointments found</p>
                      <p className="text-sm">Appointments will appear here when available</p>
                    </div>
                  </td>
                </tr>
              ) : (
                upcomingBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {booking.booking_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div>
                        <div className="font-medium">
                          {new Date(booking.appointment_date).toLocaleDateString()}
                        </div>
                        <div className="text-xs text-gray-400">
                          {booking.appointment_time}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.patient_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {booking.doctor_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        booking.status === 'Completed' ? 'bg-green-100 text-green-800' :
                        booking.status === 'Scheduled' ? 'bg-blue-100 text-blue-800' :
                        booking.status === 'In Progress' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {booking.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {booking.ecg_request_status ? (
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          booking.ecg_request_status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          booking.ecg_request_status === 'sent' ? 'bg-blue-100 text-blue-800' :
                          booking.ecg_request_status === 'received' ? 'bg-green-100 text-green-800' :
                          booking.ecg_request_status === 'failed' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {booking.ecg_request_status}
                        </span>
                      ) : (
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                          No Request
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {booking.ecg_request_status === 'received' ? (
                        <button
                          onClick={() => {
                            // Find the ECG result for this booking
                            const result = ecgResults.find(r => r.booking_id === booking.id);
                            if (result) {
                              // Show result details
                              toast.success(`ECG Result: ${result.rhythm || 'Normal'} - HR: ${result.heart_rate || 'N/A'} BPM`);
                            } else {
                              toast.info('ECG result available but details not loaded');
                            }
                          }}
                          className="text-green-600 hover:text-green-900 mr-3 flex items-center"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Result
                        </button>
                      ) : booking.ecg_request_status === 'pending' || booking.ecg_request_status === 'sent' ? (
                        <span className="text-gray-500 text-sm">
                          {booking.ecg_request_status === 'pending' ? 'Processing...' : 'Sent to Workstation'}
                        </span>
                      ) : (
                        <button
                          onClick={() => handleRequestECGForBooking(booking.id, booking.patient_id)}
                          className="bg-blue-600 text-white px-3 py-1 rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm"
                        >
                          <Heart className="h-4 w-4 mr-1" />
                          Request ECG
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ECG Requests Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent ECG Requests</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Requested
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ecgRequests.map((request) => (
                <tr key={request.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {request.request_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {request.patient_data?.patient_name || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`}>
                      {getStatusIcon(request.status)}
                      <span className="ml-1 capitalize">{request.status}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimeAgo(request.requested_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    {request.status === 'pending' && (
                      <button
                        onClick={() => handleSendRequest(request.request_id)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Send
                      </button>
                    )}
                    {request.status === 'completed' && (
                      <button
                        onClick={() => handleFetchResult(request.request_id)}
                        className="text-green-600 hover:text-green-900"
                      >
                        Fetch Result
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedRequest(request)}
                      className="text-gray-600 hover:text-gray-900"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ECG Results Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent ECG Results</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Request ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Heart Rate
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rhythm
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Received
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ecgResults.map((result) => (
                <tr key={result.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {result.request_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {result.heart_rate ? `${result.heart_rate} BPM` : 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {result.rhythm || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getResultStatusColor(result.result_status)}`}>
                      {result.result_status ? result.result_status.charAt(0).toUpperCase() + result.result_status.slice(1) : 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatTimeAgo(result.received_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button className="text-blue-600 hover:text-blue-900">
                      <Eye className="h-4 w-4" />
                    </button>
                    <button className="text-green-600 hover:text-green-900">
                      <Download className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* New Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New ECG Request</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Booking ID</label>
                  <input
                    type="number"
                    value={newRequestData.booking_id}
                    onChange={(e) => setNewRequestData({...newRequestData, booking_id: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Enter booking ID"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={newRequestData.notes}
                    onChange={(e) => setNewRequestData({...newRequestData, notes: e.target.value})}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="3"
                    placeholder="Additional notes (optional)"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowRequestModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateRequest}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Create Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ECGDeviceMonitor;
